import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import { campaignLabel, platformLabel } from '../../../components/campaigns/campaignLabel';
import { formatDate } from '../../../utils/formatters';

// With `getRowHeight="auto"` (the campaign cell has two lines), DataGrid stops
// vertically centering the other cells; this restores it (same as roles/columns.js).
function CenteredCell({ children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      {children}
    </Box>
  );
}

// Ad thumbnail, falling back to an icon when the ad has no (or an expired) image.
export function CampaignThumbnail({ campaign }) {
  return (
    <Avatar variant="rounded" src={campaign.image_url || undefined} alt="" sx={{ bgcolor: 'action.hover', color: 'text.secondary' }}>
      <AdsClickIcon fontSize="small" />
    </Avatar>
  );
}

// Only the API's ordering_fields (created_at, lead_count, headline) are sortable.
const countColumn = (t, field, key, sortable = false) => ({
  field,
  headerName: t(`campaigns.leadStats.${key}`),
  width: 115,
  type: 'number',
  align: 'left',
  headerAlign: 'left',
  sortable,
  renderCell: (params) => <CenteredCell>{params.value ?? 0}</CenteredCell>,
});

export const buildCampaignColumns = ({ t, language }) => [
  {
    // Sorting this column sorts by headline, which is what most campaigns show.
    field: 'headline',
    headerName: t('campaigns.fields.campaign'),
    flex: 1,
    minWidth: 240,
    resizable: false,
    renderCell: (params) => (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1, minWidth: 0 }}>
        <CampaignThumbnail campaign={params.row} />
        <Stack spacing={0} sx={{ minWidth: 0 }}>
          <Link component={RouterLink} to={`/campaigns/${params.row.uuid}`} variant="body2" underline="hover" noWrap>
            {campaignLabel(params.row)}
          </Link>
          <Typography variant="caption" color="text.secondary" noWrap>
            {params.row.external_id}
          </Typography>
        </Stack>
      </Stack>
    ),
  },
  {
    field: 'platform',
    headerName: t('campaigns.fields.platform'),
    width: 120,
    sortable: false,
    valueGetter: (value) => platformLabel(value),
    renderCell: (params) => <CenteredCell>{params.value}</CenteredCell>,
  },
  countColumn(t, 'lead_count', 'leadCount', true),
  countColumn(t, 'leads_open', 'leadsOpen'),
  countColumn(t, 'leads_in_progress', 'leadsInProgress'),
  countColumn(t, 'leads_closed', 'leadsClosed'),
  countColumn(t, 'leads_expired', 'leadsExpired'),
  {
    field: 'created_at',
    headerName: t('campaigns.fields.createdAt'),
    width: 120,
    renderCell: (params) => <CenteredCell>{formatDate(params.value, language)}</CenteredCell>,
  },
];
